import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Award, CheckCircle2, Info, ShieldCheck, Sparkles } from "lucide-react";
import { PageHero } from "./About";

const overviewParagraphs = [
  "Standards Club in association with Bureau of Indian Standards (BIS), Government of India, Hubballi branch is inaugurated in KLS VDIT Haliyal by the chief guest Mr. Virendra Kadam, CEO and Founder, DELTSYS E-FORMING, Belagavi on 24/8/2023.",
  "The Bureau of Indian Standards (BIS) is the National Standards body of India. It operates under the Ministry of Consumer Affairs, Food & Public Distribution, Government of India. BIS is responsible for the formulation, publication, and implementation of standards for various products, services, and systems to ensure their quality, safety, and reliability.",
];

const keyPoints = [
  "Quality consciousness, based on standardization, is one of the pillars of accelerated economic development, and priming students to appreciate the significance of quality, standards, and standardization can serve as a catalyst to improve societal awareness on these subjects.",
  "BIS has taken the initiative of creating Standards Clubs in educational institutions comprising teachers and students as members. Through a variety of activities under these Clubs, young talents get learning opportunities in the field of quality and standardization.",
  "Students are introduced to the concepts of Quality and Standardization with activities like Standards Writing, Quiz Competitions, Essay Writing, Poster Making, etc. in these clubs.",
  "Standards Club members are also provided with an opportunity to visit industries and laboratories for practical understanding, complementing the theoretical study as part of the curriculum. This allows the students to appreciate the application of science concepts and understand their importance.",
];

const benefits = [
  {
    title: "Consumer Protection",
    description:
      "BIS standards and certifications provide consumers with confidence that the products they purchase meet specific quality and safety standards.",
  },
  {
    title: "Industrial Growth",
    description:
      "Standardization helps industries produce consistent and high-quality products, which can improve their competitiveness in domestic and international markets.",
  },
  {
    title: "Safety",
    description:
      "BIS standards often focus on safety aspects, ensuring that products and services are designed and manufactured with safety in mind.",
  },
  {
    title: "Trade Facilitation",
    description:
      "BIS standards align with international standards in many cases, facilitating trade and commerce by ensuring compatibility with global markets.",
  },
  {
    title: "Quality Improvement",
    description:
      "BIS plays a role in promoting quality improvement across various sectors by setting benchmarks that companies strive to meet or exceed.",
  },
  {
    title: "Environmental Impact",
    description:
      "BIS standards can also include guidelines for environmental sustainability, contributing to responsible and eco-friendly practices.",
  },
];

const quickFacts = [
  ["Club", "Standards Club"],
  ["Association", "Bureau of Indian Standards (BIS)"],
  ["Branch", "Government of India, Hubballi branch"],
  ["Inaugurated at", "KLS VDIT Haliyal"],
  ["Chief guest", "Mr. Virendra Kadam, CEO and Founder, DELTSYS E-FORMING, Belagavi"],
  ["Date", "24/8/2023"],
];

const certificates = [
  { title: "World Standards Day 2025 Certificate and Token of Appreciation", image: "/StandardsClub_certificates/certificate.jpg" },
];
const events = [
  {
    title: "Standards Club- seminar on 'Indian Standard codes for Civil Engineering'.",
    date: "08-12-2023",
    description:
      "An activity conducted to create awareness about the importance of standards, quality, safety, and standardization among students.",
    report: "/StandardsClub/Events/Indian standard codes for Civil Engg..JPG",
  },
  {
    title: "Standards Club- Overview of BIS and its introduction",
    date: "08-12-2023",
    description:
      "A student-focused activity introducing the process of standards writing and the role of BIS in quality and standardization.",
    report: "/StandardsClub/Events/Overview of BIS and its introduction.JPG",
  },
  {
    title: "Standards Club Quiz Competition (18-01-2024)",
    date: "18-01-2024",
    description:
      "A quiz-based learning activity to help students understand quality, safety, consumer protection, and standardization concepts.",
    report: "/StandardsClub/Events/Quiz-competition.JPG",
  },
  {
    title: "Standards Club Quiz Competition (30-10-2023)",
    date: "30-10-2023",
    description:
      "A practical exposure visit organized to help students understand the application of standards in industries and laboratories.",
    report: "/StandardsClub/Events/Standards Club (SC-4304), KLS VDIT, Haliyal in BIS, Government of India, Hubballi branch has organized the Quiz Competition.JPG",
  },
  {
    title: "Inauguration and Orientation Program",
    date: "24-08-2023",
    description:
      "A practical exposure visit organized to help students understand the application of standards in industries and laboratories.",
    report: "/StandardsClub/Events/Standards Club in association with Bureau of Indian Standards (BIS), Government of India, Hubballi branch is inaugurated in KLS VDIT Haliyal.JPG",
  },
];
const StandardsClub = () => (
  <main>
    <PageHero
      title="Standards Club"
      sub="Bureau of Indian Standards (BIS), quality consciousness, standardization, and student learning"
      image="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=2400&q=80"
    />

    <section className="max-w-7xl mx-auto px-6 lg:px-10 py-14">
      <Link
        to="/clubs"
        className="inline-flex items-center gap-2 text-sm font-semibold text-brand hover:underline mb-8"
      >
        <ArrowLeft size={16} />
        Back to Clubs
      </Link>

      <div className="grid lg:grid-cols-3 gap-8 items-start">
        <div className="lg:col-span-2 space-y-8">
          <section className="bg-white border border-brand/15 p-7 md:p-9">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-11 h-11 border border-brand/20 flex items-center justify-center text-brand">
                <ShieldCheck size={22} />
              </div>
              <p className="text-xs uppercase tracking-[0.24em] text-brand font-semibold">
                Standards Club
              </p>
            </div>

            <h1
              className="text-3xl md:text-5xl text-brand font-semibold leading-tight"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Standards Club
            </h1>

            <div className="mt-5 space-y-4">
              {overviewParagraphs.map((paragraph) => (
                <p key={paragraph} className="text-[16px] text-[#2a2a2a]/85 leading-relaxed">
                  {paragraph}
                </p>
              ))}
            </div>
          </section>

          <section className="bg-white border border-brand/15 p-7 md:p-9">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-11 h-11 border border-brand/20 flex items-center justify-center text-brand">
                <Award size={22} />
              </div>
              <h2
                className="text-3xl md:text-4xl text-brand font-semibold leading-tight"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                Standards Club Certificates
              </h2>
            </div>

            <div className="space-y-8">
              {certificates.map((certificate) => (
                <figure key={certificate.image}>
                  <figcaption
                    className="mb-5 text-2xl md:text-3xl text-brand font-semibold leading-tight"
                    style={{ fontFamily: "'Cormorant Garamond', serif" }}
                  >
                    {certificate.title}
                  </figcaption>
                  <div className="bg-surface/40 border border-brand/10 p-3 md:p-5">
                    <img
                      src={certificate.image}
                      alt={certificate.title}
                      className="w-full h-auto max-h-[760px] object-contain bg-white"
                      loading="lazy"
                    />
                  </div>
                </figure>
              ))}
            </div>
          </section>
          
          <section className="bg-white border border-brand/15 p-7 md:p-9">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-11 h-11 border border-brand/20 flex items-center justify-center text-brand">
                <CheckCircle2 size={22} />
              </div>
              <h2
                className="text-3xl md:text-4xl text-brand font-semibold leading-tight"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                Key points about BIS:
              </h2>
            </div>

            <ul className="space-y-3">
              {keyPoints.map((point) => (
                <li
                  key={point}
                  className="flex items-start gap-3 bg-surface-alt/40 border border-brand/10 p-4"
                >
                  <CheckCircle2 size={18} className="text-brand flex-shrink-0 mt-0.5" />
                  <span className="text-[15.5px] text-[#2a2a2a]/85 leading-relaxed">
                    {point}
                  </span>
                </li>
              ))}
            </ul>
          </section>

          <section className="bg-white border border-brand/15 p-7 md:p-9">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-11 h-11 border border-brand/20 flex items-center justify-center text-brand">
                <Sparkles size={22} />
              </div>
              <h2
                className="text-3xl md:text-4xl text-brand font-semibold leading-tight"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                Benefits of BIS:
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              {benefits.map((benefit) => (
                <article key={benefit.title} className="bg-surface-alt/35 border border-brand/10 p-5">
                  <h3 className="text-xl text-brand font-semibold">{benefit.title}</h3>
                  <p className="mt-2 text-[15px] text-[#2a2a2a]/85 leading-relaxed">
                    {benefit.description}
                  </p>
                </article>
              ))}
            </div>
          </section>

          <section className="bg-white border border-brand/15 p-7 md:p-9">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-11 h-11 border border-brand/20 flex items-center justify-center text-brand">
                <Award size={22} />
              </div>
              <h2
                className="text-3xl md:text-4xl text-brand font-semibold leading-tight"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                Events & Reports
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-5">
              {events.map((event) => (
                <article
                  key={event.title}
                  className="bg-surface-alt/35 border border-brand/10 p-5"
                >
                  <p className="text-xs uppercase tracking-[0.22em] text-brand font-semibold">
                    {event.date}
                  </p>

                  <h3 className="text-xl text-brand font-semibold mt-2">
                    {event.title}
                  </h3>

                  <p className="mt-2 text-[15px] text-[#2a2a2a]/85 leading-relaxed">
                    {event.description}
                  </p>

                  {/* <a
                    href={event.report}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 mt-4 bg-brand text-white px-4 py-2 text-xs font-semibold tracking-wider uppercase hover:bg-brand-dark transition"
                  >
                    <Award size={14} />
                    View Report
                  </a> */}
                </article>
              ))}
            </div>
          </section>

        </div>


        <aside className="space-y-5 lg:sticky lg:top-32">
          <div className="bg-white border border-brand/15 p-5">
            <h2 className="text-xs uppercase tracking-[0.24em] text-brand font-semibold">
              Quick Facts
            </h2>
            <div className="mt-4 space-y-3 text-sm">
              {quickFacts.map(([label, value]) => (
                <div key={label} className="flex items-start gap-3">
                  <Info size={15} className="text-brand flex-shrink-0 mt-0.5" />
                  <p className="text-[#2a2a2a]/85 leading-snug">
                    {label}: <span className="font-semibold text-[#2a2a2a]">{value}</span>
                  </p>
                </div>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </section>
  </main>
);

export default StandardsClub;